import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { fmtDate, toInputDate } from '../utils';
import { Modal, ConfirmDialog } from '../components/ui';

const STATUS_OPTIONS      = ['Open', 'Filled', 'Unable to Staff', 'Lost'];
const FILL_SOURCE_OPTIONS = ['Internal FTE', 'External FTE', '1099'];
const DURATION_OPTIONS    = [3, 6, 9, 12, 18, 24, 36];
const CANDIDATE_STATUS_OPTIONS = [
  'Identified', 'Evaluating', 'Client Interview',
  'Pending Offer', 'Offer Rolled Out', 'Onboarding', 'Joined',
];

function CandidatePill({ status }) {
  if (!status) return <span style={{ color:'var(--text-muted)' }}>—</span>;
  const colors = {
    'Identified':       { bg: 'var(--bg-hover)',          fg: 'var(--text-muted)' },
    'Evaluating':       { bg: 'var(--primary-light)',     fg: 'var(--primary)' },
    'Client Interview': { bg: 'rgba(124,58,237,0.12)',    fg: '#7c3aed' },
    'Pending Offer':    { bg: 'var(--warning-light)',     fg: 'var(--warning)' },
    'Offer Rolled Out': { bg: '#ffedd5',                  fg: '#c2410c' },
    'Onboarding':       { bg: '#cffafe',                  fg: '#0891b2' },
    'Joined':           { bg: 'var(--success-light)',     fg: 'var(--success)' },
  };
  const c = colors[status] || { bg: 'var(--bg-hover)', fg: 'var(--text-muted)' };
  return (
    <span style={{
      display:'inline-flex', padding:'2px 9px', borderRadius:20,
      fontSize:11, fontWeight:600, background:c.bg, color:c.fg,
    }}>{status}</span>
  );
}

function StatusPill({ status }) {
  const colors = {
    'Open':            { bg: 'var(--primary-light)', fg: 'var(--primary)' },
    'Filled':          { bg: 'var(--success-light)', fg: 'var(--success)' },
    'Unable to Staff': { bg: 'var(--bg-hover)',      fg: 'var(--text-muted)' },
    'Lost':            { bg: 'var(--danger-light)',  fg: 'var(--danger)' },
  };
  const c = colors[status] || { bg: 'var(--bg-hover)', fg: 'var(--text-muted)' };
  return (
    <span style={{
      display:'inline-flex', padding:'2px 9px', borderRadius:20,
      fontSize:11, fontWeight:600, background:c.bg, color:c.fg,
    }}>{status}</span>
  );
}

function Tile({ label, value, sub, color }) {
  return (
    <div className="kpi-card" style={color ? { borderTop:`3px solid ${color}` } : undefined}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

const fmtMoney = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct   = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return v;
}

export function PositionForm({ position, opportunities, tiers, canSeeMargin, lockedOpportunityId, lockedOpportunityName, onSave, onClose }) {
  const isEdit = !!position?.id;
  const [form, setForm] = useState({
    role_title: '',
    role_tier_id: '',
    opportunity_id: '',
    status: 'Open',
    open_date: toInputDate(new Date().toISOString()),
    target_start_date: '',
    actual_start_date: '',
    duration_months: 12,
    fill_source: '',
    filled_by_name: '',
    bill_rate: '',
    offered_base: '',
    offered_bonus: '',
    joining_bonus: 0,
    sub_hourly_rate: '',
    notes: '',
    ...position,
    open_date:         toInputDate(position?.open_date) || toInputDate(new Date().toISOString()),
    target_start_date: toInputDate(position?.target_start_date),
    actual_start_date: toInputDate(position?.actual_start_date),
    role_tier_id:      position?.role_tier_id   || '',
    opportunity_id:    lockedOpportunityId || position?.opportunity_id || '',
    fill_source:       position?.fill_source    || '',
    candidate_status:  position?.candidate_status || '',
    bill_rate:         position?.bill_rate         ?? '',
    offered_base:      position?.offered_base      ?? '',
    offered_bonus:     position?.offered_bonus     ?? '',
    joining_bonus:     position?.joining_bonus     ?? 0,
    sub_hourly_rate:   position?.sub_hourly_rate   ?? '',
  });
  const [config, setConfig] = useState(null);   // {bonus_pct, ...} fetched once
  const [margin, setMargin] = useState(null);
  const [marginError, setMarginError] = useState('');
  const [bonusEdited, setBonusEdited] = useState(isEdit && position?.offered_bonus != null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmLockIn, setConfirmLockIn] = useState(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const selectedTier = useMemo(
    () => tiers.find(t => t.id === form.role_tier_id) || null,
    [tiers, form.role_tier_id]
  );
  const tierBaseline = selectedTier?.baseline_base != null ? Number(selectedTier.baseline_base) : null;
  const isFTE        = form.fill_source === 'Internal FTE' || form.fill_source === 'External FTE';
  const is1099       = form.fill_source === '1099';
  const usingBaseline = form.fill_source === 'Internal FTE' && (form.offered_base === '' || form.offered_base == null) && tierBaseline != null;

  // Load config once (for bonus auto-default).
  useEffect(() => {
    if (!canSeeMargin) return;
    api.get('/config').then(r => {
      const cfg = {};
      r.data.forEach(row => {
        cfg[row.key] = row.value_type === 'int' ? parseInt(row.value, 10) : parseFloat(row.value);
      });
      setConfig(cfg);
    }).catch(() => {});
  }, [canSeeMargin]);

  // Auto-default offered_bonus = base × bonus_pct unless the user has explicitly edited it.
  useEffect(() => {
    if (!canSeeMargin || !config || bonusEdited) return;
    if (form.fill_source !== 'Internal FTE' && form.fill_source !== 'External FTE') return;
    const baseForBonus = form.offered_base !== '' && form.offered_base != null
      ? Number(form.offered_base)
      : (form.fill_source === 'Internal FTE' ? tierBaseline : null);
    if (baseForBonus == null) return;
    const expected = Math.round(baseForBonus * Number(config.bonus_pct));
    setForm(f => ({ ...f, offered_bonus: expected }));
  }, [canSeeMargin, config, bonusEdited, form.fill_source, form.offered_base, tierBaseline]);

  // Live margin via /calc-margin (debounced).
  const calcInputs = useDebounced({
    fill_source:     form.fill_source,
    tier_id:         form.role_tier_id || null,
    offered_base:    form.offered_base,
    offered_bonus:   form.offered_bonus,
    joining_bonus:   form.joining_bonus,
    sub_hourly_rate: form.sub_hourly_rate,
    duration_months: Number(form.duration_months) || 12,
    bill_rate:       form.bill_rate,
  }, 400);

  useEffect(() => {
    if (!canSeeMargin) return;
    if (!calcInputs.fill_source) { setMargin(null); return; }
    const billRate = Number(calcInputs.bill_rate);
    if (!billRate) { setMargin(null); return; }
    const isFTECalc = calcInputs.fill_source === 'Internal FTE' || calcInputs.fill_source === 'External FTE';
    const is1099Calc = calcInputs.fill_source === '1099';
    if (is1099Calc && !calcInputs.sub_hourly_rate) { setMargin(null); return; }
    if (isFTECalc) {
      const hasBase = (calcInputs.offered_base !== '' && calcInputs.offered_base != null) ||
                      (calcInputs.fill_source === 'Internal FTE' && calcInputs.tier_id);
      if (!hasBase) { setMargin(null); return; }
    }

    setMarginError('');
    api.post('/comp-plan/calc-margin', calcInputs)
      .then(r => setMargin(r.data))
      .catch(e => { setMargin(null); setMarginError(e.response?.data?.error || 'Calc failed'); });
  }, [calcInputs, canSeeMargin]);

  const handleSubmit = async (lockInBase = null) => {
    if (!form.role_title?.trim()) return setError('Role title required');
    setSaving(true); setError('');
    try {
      const payload = {
        role_title:        form.role_title.trim(),
        role_tier_id:      form.role_tier_id || null,
        opportunity_id:    form.opportunity_id || null,
        status:            form.status,
        open_date:         form.open_date         || null,
        target_start_date: form.target_start_date || null,
        actual_start_date: form.actual_start_date || null,
        duration_months:   Number(form.duration_months) || null,
        fill_source:       form.fill_source       || null,
        filled_by_name:    form.filled_by_name    || null,
        candidate_status:  form.candidate_status  || null,
        notes:             form.notes             || null,
      };
      if (canSeeMargin) {
        payload.bill_rate       = form.bill_rate       === '' ? null : Number(form.bill_rate);
        payload.offered_base    = lockInBase != null ? lockInBase : (form.offered_base    === '' ? null : Number(form.offered_base));
        payload.offered_bonus   = form.offered_bonus   === '' ? null : Number(form.offered_bonus);
        payload.joining_bonus   = form.joining_bonus   === '' ? 0    : Number(form.joining_bonus);
        payload.sub_hourly_rate = form.sub_hourly_rate === '' ? null : Number(form.sub_hourly_rate);
      }
      const r = isEdit
        ? await api.put(`/staffing/${position.id}`, payload)
        : await api.post('/staffing', payload);
      onSave(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = () => {
    // Lock-in prompt: flipping to Filled with no override on Internal FTE that uses baseline.
    if (form.status === 'Filled' && form.fill_source === 'Internal FTE'
        && (form.offered_base === '' || form.offered_base == null) && tierBaseline != null) {
      setConfirmLockIn(tierBaseline);
      return;
    }
    handleSubmit();
  };

  return (
    <Modal title={isEdit ? 'Edit Position' : 'New Position'} onClose={onClose} size="modal-lg">
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}

        <div className="section-divider">Role & Status</div>
        <div className="form-group"><label className="form-label">Role Title *</label>
          <input className="form-input" value={form.role_title} onChange={set('role_title')} placeholder="e.g. Senior ServiceNow Architect" />
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Role Tier</label>
            <select className="form-select" value={form.role_tier_id} onChange={set('role_tier_id')}>
              <option value="">— None —</option>
              {tiers.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.tier_name}{t.baseline_base != null ? ` ($${Number(t.baseline_base).toLocaleString()})` : ''}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={set('status')}>
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          {lockedOpportunityId ? (
            <div className="form-group"><label className="form-label">Opportunity</label>
              <input className="form-input" value={lockedOpportunityName || 'This opportunity'} disabled style={{ background:'var(--bg-hover)', color:'var(--text-muted)' }} />
            </div>
          ) : (
            <div className="form-group"><label className="form-label">Opportunity</label>
              <select className="form-select" value={form.opportunity_id} onChange={set('opportunity_id')}>
                <option value="">— None (bench / anticipated) —</option>
                {opportunities.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div className="form-group"><label className="form-label">Fill Source</label>
            <select className="form-select" value={form.fill_source} onChange={set('fill_source')}>
              <option value="">— Not set —</option>
              {FILL_SOURCE_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Candidate Stage</label>
            <select className="form-select" value={form.candidate_status} onChange={set('candidate_status')}>
              <option value="">— Not started —</option>
              {CANDIDATE_STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          {form.candidate_status && (
            <div className="form-group"><label className="form-label">Candidate Name</label>
              <input className="form-input" value={form.filled_by_name} onChange={set('filled_by_name')} placeholder="Person being evaluated" />
            </div>
          )}
        </div>

        <div className="section-divider">Dates & Duration</div>
        <div className="form-grid-3">
          <div className="form-group"><label className="form-label">Open Date</label>
            <input className="form-input" type="date" value={form.open_date} onChange={set('open_date')} />
          </div>
          <div className="form-group"><label className="form-label">Target Start Date</label>
            <input className="form-input" type="date" value={form.target_start_date} onChange={set('target_start_date')} />
          </div>
          <div className="form-group"><label className="form-label">Duration (months)</label>
            <select className="form-select" value={form.duration_months} onChange={set('duration_months')}>
              {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} months</option>)}
            </select>
          </div>
        </div>
        {form.status === 'Filled' && (
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Actual Start Date</label>
              <input className="form-input" type="date" value={form.actual_start_date} onChange={set('actual_start_date')} />
            </div>
            {!form.candidate_status && (
              <div className="form-group"><label className="form-label">Filled By Name</label>
                <input className="form-input" value={form.filled_by_name} onChange={set('filled_by_name')} placeholder="Person filling the role" />
              </div>
            )}
          </div>
        )}

        {canSeeMargin && (
          <>
            <div className="section-divider">Compensation & Margin</div>
            <div className="form-group"><label className="form-label">Bill Rate ($/hr)</label>
              <input className="form-input" type="number" value={form.bill_rate} onChange={set('bill_rate')} placeholder="e.g. 150" />
            </div>

            {isFTE && (
              <>
                <div className="form-group">
                  <label className="form-label">
                    Offered Base ($/yr){form.fill_source === 'External FTE' ? ' *' : ' (override)'}
                  </label>
                  <input className="form-input" type="number" value={form.offered_base} onChange={set('offered_base')}
                         placeholder={usingBaseline ? `Using ${selectedTier?.tier_name} baseline: $${tierBaseline.toLocaleString()}` : 'Enter annual base salary'} />
                  {usingBaseline && (
                    <div style={{ fontSize:11, color:'var(--primary)', marginTop:4 }}>
                      ✨ Using tier baseline (${tierBaseline.toLocaleString()}). Type a value above to override.
                    </div>
                  )}
                  {form.fill_source === 'Internal FTE' && !tierBaseline && !form.offered_base && (
                    <div style={{ fontSize:11, color:'var(--warning)', marginTop:4 }}>
                      No baseline on this tier. Set one in Settings → Comp Plan, or type a value above.
                    </div>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Annual Bonus ($)</label>
                    <input className="form-input" type="number"
                           value={form.offered_bonus}
                           onChange={e => { setBonusEdited(true); setForm(f => ({ ...f, offered_bonus: e.target.value })); }}
                           placeholder={config ? `Defaults to ${(config.bonus_pct * 100).toFixed(0)}% of base` : 'Annual performance bonus'} />
                    {!bonusEdited && config && <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>Auto-default: base × {(config.bonus_pct * 100).toFixed(0)}%. Type to override.</div>}
                  </div>
                  <div className="form-group"><label className="form-label">Joining Bonus ($, one-time)</label>
                    <input className="form-input" type="number" value={form.joining_bonus} onChange={set('joining_bonus')} placeholder="0" />
                    <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>Amortized over duration in margin calc.</div>
                  </div>
                </div>
              </>
            )}

            {is1099 && (
              <div className="form-group"><label className="form-label">Sub Hourly Rate ($/hr) *</label>
                <input className="form-input" type="number" value={form.sub_hourly_rate} onChange={set('sub_hourly_rate')} placeholder="e.g. 80" />
              </div>
            )}

            {/* Live margin panel */}
            <div style={{ marginTop:12, padding:'12px 16px', background:'var(--bg-hover)', borderRadius:8, border:'1px solid var(--border-color)' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>Live Margin Preview</div>
              {marginError && <div style={{ fontSize:12, color:'var(--danger)' }}>{marginError}</div>}
              {!margin && !marginError && <div style={{ fontSize:13, color:'var(--gray-500)' }}>Fill in bill rate and compensation to see margin.</div>}
              {margin && (
                <div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:14 }}>
                    <div style={{
                      fontSize:32, fontWeight:800, lineHeight:1,
                      color: margin.marginPct >= 0.30 ? 'var(--success)' : margin.marginPct >= 0.15 ? 'var(--warning)' : 'var(--danger)',
                    }}>
                      {fmtPct(margin.marginPct)}
                    </div>
                    <div style={{ fontSize:14, color:'var(--text-muted)' }}>
                      {fmtMoney(margin.marginDollars)} / yr
                    </div>
                  </div>
                  {margin.effective_base_source === 'baseline' && (
                    <div style={{ marginTop:8, fontSize:11, color:'var(--primary)' }}>
                      ✨ Computed from tier baseline (${(margin.effective_base ?? 0).toLocaleString()}). Editing the tier will update this margin live.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="section-divider">Notes</div>
        <div className="form-group">
          <textarea className="form-textarea" value={form.notes} onChange={set('notes')} rows={3} placeholder="Anything worth flagging — clearance reqs, location, must-haves..." />
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={onSaveClick} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update Position' : 'Create Position'}</button>
      </div>

      {confirmLockIn != null && (
        <ConfirmDialog
          message={`This position uses the tier baseline ($${confirmLockIn.toLocaleString()}). Once Filled, this should be locked in as the actual offered salary so the position no longer drifts when the baseline changes. Lock in $${confirmLockIn.toLocaleString()}?`}
          confirmLabel="Lock in baseline"
          danger={false}
          onConfirm={() => { setConfirmLockIn(null); handleSubmit(confirmLockIn); }}
          onCancel={() => setConfirmLockIn(null)}
        />
      )}
    </Modal>
  );
}

// Embedded view: positions list scoped to a single opportunity.
// Reuses the shared PositionForm. Pre-selects opportunity_id and locks the field.
export function OpportunityStaffingTab({ opportunityId, opportunityName }) {
  const { user, can } = useAuth();
  const canSeeMargin = !!(user?.is_owner || user?.can_view_comp_plan || user?.can_view_staffing);
  const [positions, setPositions] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tiers,     setTiers]     = useState([]);
  const [showForm,  setShowForm]  = useState(false);
  const [editPos,   setEditPos]   = useState(null);
  const [deleteId,  setDeleteId]  = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/staffing', { params: { opportunity_id: opportunityId } })
      .then(r => setPositions(r.data))
      .finally(() => setLoading(false));
  }, [opportunityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (canSeeMargin) {
      api.get('/comp-plan/tiers').then(r => setTiers(r.data)).catch(() => setTiers([]));
    }
  }, [canSeeMargin]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/staffing/${deleteId}`); }
    catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
    setDeleteId(null);
    load();
  };

  const showActions = can('edit');
  const baseCols    = 5; // Role, Status, Target Start, Duration, Candidate
  const compCols    = canSeeMargin ? 2 : 0;
  const colSpan     = baseCols + compCols + (showActions ? 1 : 0);

  // Forecast-revenue rollup for this opportunity (full duration only, not year-scoped here).
  const totalForecast = canSeeMargin
    ? positions.reduce((sum, p) => sum + (p.forecast_engagement_revenue || 0), 0)
    : null;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <span className="card-title">Positions</span>
          <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
            Open and filled roles for this opportunity
            {canSeeMargin && totalForecast > 0 && (
              <> · Total forecast revenue: <strong style={{ color:'var(--success)' }}>{fmtMoney(totalForecast)}</strong></>
            )}
          </div>
        </div>
        {can('edit') && (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditPos(null); setShowForm(true); }}>+ Add Position</button>
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Status</th>
            <th>Target Start</th>
            <th>Duration</th>
            <th>Candidate</th>
            {canSeeMargin && <th>Bill Rate</th>}
            {canSeeMargin && <th>Margin %</th>}
            {showActions && <th style={{ width:80 }}></th>}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={colSpan} style={{ textAlign:'center', padding:24, color:'var(--gray-400)' }}>Loading...</td></tr>}
          {!loading && positions.length === 0 && (
            <tr><td colSpan={colSpan}>
              <div className="empty-state" style={{ padding:24 }}>
                <div className="empty-state-icon">👥</div>
                <div className="empty-state-text">No positions yet for this opportunity</div>
              </div>
            </td></tr>
          )}
          {!loading && positions.map(p => (
            <tr key={p.id}
                onClick={() => { if (showActions) { setEditPos(p); setShowForm(true); } }}
                style={{ cursor: showActions ? 'pointer' : 'default' }}>
              <td style={{ fontWeight:500 }}>{p.role_title}</td>
              <td><StatusPill status={p.status} /></td>
              <td style={{ fontSize:13 }}>{fmtDate(p.target_start_date)}</td>
              <td style={{ fontSize:13 }}>{p.duration_months ? `${p.duration_months} mo` : '—'}</td>
              <td style={{ fontSize:13 }}>{p.filled_by_name || '—'}</td>
              {canSeeMargin && <td style={{ fontSize:13 }}>{p.bill_rate != null ? `$${p.bill_rate}/hr` : '—'}</td>}
              {canSeeMargin && (
                <td style={{
                  fontSize:13, fontWeight:600,
                  color: p.margin_pct == null ? 'var(--text-muted)'
                       : p.margin_pct >= 0.30 ? 'var(--success)'
                       : p.margin_pct >= 0.15 ? 'var(--warning)'
                       : 'var(--danger)',
                }}>
                  {fmtPct(p.margin_pct)}
                </td>
              )}
              {showActions && (
                <td onClick={e => e.stopPropagation()} style={{ display:'flex', gap:4, paddingTop:14 }}>
                  <button className="btn btn-secondary btn-sm btn-icon" title="Edit" onClick={() => { setEditPos(p); setShowForm(true); }}>✏️</button>
                  <button className="btn btn-secondary btn-sm btn-icon" title="Delete" onClick={() => setDeleteId(p.id)} style={{ color:'var(--danger)' }}>🗑️</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <PositionForm
          position={editPos}
          opportunities={[]}
          tiers={tiers}
          canSeeMargin={canSeeMargin}
          lockedOpportunityId={opportunityId}
          lockedOpportunityName={opportunityName}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); load(); }}
        />
      )}
      {deleteId && (
        <ConfirmDialog
          message="Delete this position permanently? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}

export default function Staffing() {
  const { user, can } = useAuth();
  const canSeeMargin = !!(user?.is_owner || user?.can_view_comp_plan || user?.can_view_staffing);

  const [positions, setPositions]    = useState([]);
  const [summary,   setSummary]      = useState(null);
  const [loading,   setLoading]      = useState(true);
  const [filters,   setFilters]      = useState({ status: '', fill_source: '', candidate_status: '', search: '', year: String(new Date().getFullYear()) });
  const [opportunities, setOpportunities] = useState([]);
  const [tiers,         setTiers]         = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [editPos,  setEditPos]    = useState(null);
  const [deleteId, setDeleteId]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status)      params.status      = filters.status;
      if (filters.fill_source) params.fill_source = filters.fill_source;
      if (filters.year)        params.year        = filters.year;
      const [posR, sumR] = await Promise.all([
        api.get('/staffing', { params }),
        api.get('/staffing/summary', { params: filters.year ? { year: filters.year } : {} }),
      ]);
      setPositions(posR.data);
      setSummary(sumR.data);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.fill_source, filters.year]);

  useEffect(() => { load(); }, [load]);

  // Load opportunity + tier dropdowns once. Tiers only fetchable by canSeeMargin users.
  useEffect(() => {
    api.get('/opportunities', { params: { limit: 500 } })
      .then(r => setOpportunities(r.data?.data || r.data || []))
      .catch(() => setOpportunities([]));
    if (canSeeMargin) {
      api.get('/comp-plan/tiers').then(r => setTiers(r.data)).catch(() => setTiers([]));
    }
  }, [canSeeMargin]);

  const openNew  = () => { setEditPos(null); setShowForm(true); };
  const openEdit = (p) => { setEditPos(p);   setShowForm(true); };
  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/staffing/${deleteId}`); }
    catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
    setDeleteId(null);
    load();
  };

  const filtered = positions.filter(p => {
    if (filters.candidate_status && p.candidate_status !== filters.candidate_status) return false;
    if (filters.search && !p.role_title?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const showActions = can('edit');
  const showYearCol = !!filters.year;
  const compCols    = canSeeMargin ? (showYearCol ? 3 : 2) : 0; // Bill Rate, [Year Rev], Margin %
  const colSpan = 6 + compCols + (showActions ? 1 : 0); // Role, Status, Opportunity, Target Start, Duration, Candidate

  return (
    <>
      <div className="page-header">
        <span className="page-title">Staffing</span>
        <div style={{ display:'flex', gap:8 }}>
          {can('edit') && (
            <button className="btn btn-primary" onClick={openNew}>+ New Position</button>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Dashboard tiles — year-scoped */}
        <div className="kpi-grid">
          <Tile label="Open"
                value={summary?.open_count   ?? '—'}
                sub={filters.year ? `Engagement overlaps ${filters.year}` : 'All time'}
                color="var(--primary)" />
          <Tile label="Filled"
                value={summary?.filled_count ?? '—'}
                sub={filters.year ? `Engagement overlaps ${filters.year}` : 'All time'}
                color="var(--success)" />
          <Tile label="Avg Days to Fill"
                value={summary?.avg_days_to_fill != null ? `${summary.avg_days_to_fill}d` : '—'}
                sub={filters.year ? `Filled in ${filters.year}` : 'Last 90 days'} />
          {canSeeMargin && (
            <Tile label="Total Forecast Revenue"
                  value={fmtMoney(summary?.forecast_revenue_total)}
                  sub="Open + Filled, full engagement"
                  color="var(--success)" />
          )}
          {canSeeMargin && filters.year && (
            <Tile label={`${filters.year} Revenue`}
                  value={fmtMoney(summary?.forecast_revenue_year)}
                  sub={`Forecast portion in ${filters.year}`}
                  color="var(--primary)" />
          )}
        </div>

        {/* Filters */}
        <div style={{ marginBottom:16, marginTop:20 }}>
          <div className="toolbar">
            <input
              className="search-input"
              placeholder="Search by role title..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            <select className="filter-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="filter-select" value={filters.fill_source} onChange={e => setFilters(f => ({ ...f, fill_source: e.target.value }))}>
              <option value="">All Fill Sources</option>
              {FILL_SOURCE_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="filter-select" value={filters.candidate_status} onChange={e => setFilters(f => ({ ...f, candidate_status: e.target.value }))}>
              <option value="">All Candidate Stages</option>
              {CANDIDATE_STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="filter-select" value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))}>
              <option value="">All Years</option>
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
            <span className="text-muted text-sm" style={{ marginLeft:'auto' }}>
              {filtered.length} position{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Opportunity</th>
                  <th>Target Start</th>
                  <th>Duration</th>
                  <th>Candidate</th>
                  {canSeeMargin && <th>Bill Rate</th>}
                  {canSeeMargin && showYearCol && <th>{filters.year} Revenue</th>}
                  {canSeeMargin && <th>Margin %</th>}
                  {showActions && <th style={{ width:80 }}></th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={colSpan} style={{ textAlign:'center', padding:40, color:'var(--gray-400)' }}>Loading...</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={colSpan}>
                    <div className="empty-state">
                      <div className="empty-state-icon">👥</div>
                      <div className="empty-state-text">No positions found</div>
                    </div>
                  </td></tr>
                )}
                {!loading && filtered.map(p => (
                  <tr key={p.id} onClick={() => showActions && openEdit(p)} style={{ cursor: showActions ? 'pointer' : 'default' }}>
                    <td style={{ fontWeight:500 }}>{p.role_title}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td style={{ fontSize:13 }}>{p.opportunity_name || '—'}</td>
                    <td style={{ fontSize:13 }}>{fmtDate(p.target_start_date)}</td>
                    <td style={{ fontSize:13 }}>{p.duration_months ? `${p.duration_months} mo` : '—'}</td>
                    <td style={{ fontSize:13 }}>{p.filled_by_name || '—'}</td>
                    {canSeeMargin && <td style={{ fontSize:13 }}>{p.bill_rate != null ? `$${p.bill_rate}/hr` : '—'}</td>}
                    {canSeeMargin && showYearCol && <td style={{ fontSize:13 }}>{fmtMoney(p.year_revenue)}</td>}
                    {canSeeMargin && (
                      <td style={{
                        fontSize:13, fontWeight:600,
                        color: p.margin_pct == null ? 'var(--text-muted)'
                             : p.margin_pct >= 0.30 ? 'var(--success)'
                             : p.margin_pct >= 0.15 ? 'var(--warning)'
                             : 'var(--danger)',
                      }}>
                        {fmtPct(p.margin_pct)}
                        {p.effective_base_source === 'baseline' && <div style={{ fontSize:10, fontWeight:500, color:'var(--primary)' }}>via baseline</div>}
                      </td>
                    )}
                    {showActions && (
                      <td onClick={e => e.stopPropagation()} style={{ display:'flex', gap:4, paddingTop:14 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Edit" onClick={() => openEdit(p)}>✏️</button>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Delete" onClick={() => setDeleteId(p.id)} style={{ color:'var(--danger)' }}>🗑️</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showForm && (
        <PositionForm
          position={editPos}
          opportunities={opportunities}
          tiers={tiers}
          canSeeMargin={canSeeMargin}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); load(); }}
        />
      )}
      {deleteId && (
        <ConfirmDialog
          message="Delete this position permanently? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </>
  );
}
